// @unocss-include
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../button/index.js';
import { CollapsibleSimple } from '../collapsible/Collapsible.js';
import { Icon } from '../icon/index.js';
import { ScrollAreaRoot, ScrollAreaScrollbar, ScrollAreaViewport, } from '../scrollArea/ScrollArea.js';
export function HorizontalList(_a) {
    var { open: externalOpen, onOpenChange, children, className, contentClassName, background, onCanOpenChange, openDirection = 'down', disableInternalOpenToggle } = _a, rest = __rest(_a, ["open", "onOpenChange", "children", "className", "contentClassName", "background", "onCanOpenChange", "openDirection", "disableInternalOpenToggle"]);
    const contentRef = useRef(null);
    const containerRef = useRef(null);
    const rememberedWidth = useRef(0);
    const lastCanOpenChangeEmitted = useRef(undefined);
    const [internalOpen, setInternalOpen] = useState(false);
    const open = externalOpen !== null && externalOpen !== void 0 ? externalOpen : internalOpen;
    const toggleOpen = useCallback(() => {
        if (externalOpen === undefined) {
            setInternalOpen((prev) => !prev);
        }
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(!open);
    }, [externalOpen, onOpenChange, open]);
    const [internalCanOpen, setInternalCanOpen] = useState(false);
    const emitCanOpenChange = useCallback((canOpen) => {
        if (canOpen !== lastCanOpenChangeEmitted.current) {
            lastCanOpenChangeEmitted.current = canOpen;
            onCanOpenChange === null || onCanOpenChange === void 0 ? void 0 : onCanOpenChange(canOpen);
            setInternalCanOpen(canOpen);
        }
    }, [onCanOpenChange]);
    useEffect(() => {
        const content = contentRef.current;
        const container = containerRef.current;
        if (!content || !container) {
            return;
        }
        const contentWidth = content.offsetWidth;
        const containerHeight = container.offsetHeight;
        const containerWidth = container.offsetWidth;
        if (open && contentWidth > containerWidth) {
            // measure content width and animate to container width
            rememberedWidth.current = contentWidth;
            content.style.setProperty('width', `${containerWidth}px`);
            content.style.setProperty('flex-wrap', 'wrap');
            // force measure to get the new height
            const contentHeight = content.offsetHeight;
            container
                .animate([
                { height: `${containerHeight}px` },
                { height: `${contentHeight}px` },
            ], {
                easing: 'cubic-bezier(0.64, -0.25, 0.1, 1.4)',
                duration: 200,
            })
                .finished.then(() => {
                emitCanOpenChange(true);
            });
        }
        else {
            content.style.setProperty('width', `auto`);
            content.style.setProperty('flex-wrap', 'nowrap');
            // force measure to get new height
            const contentHeight = content.offsetHeight;
            container
                .animate([
                {
                    height: `${containerHeight}px`,
                },
                {
                    height: `${contentHeight}px`,
                },
            ], {
                easing: 'cubic-bezier(0.64, -0.25, 0.1, 1.4)',
                duration: 200,
            })
                .finished.then(() => {
                requestAnimationFrame(() => {
                    const canOpen = content.offsetWidth > container.offsetWidth;
                    emitCanOpenChange(canOpen);
                });
            });
        }
    }, [open, contentRef, containerRef]);
    useEffect(() => {
        if (!containerRef.current || !contentRef.current) {
            return;
        }
        if (open)
            return;
        const resizeObserver = new ResizeObserver(() => {
            const content = contentRef.current;
            const container = containerRef.current;
            if (!content || !container) {
                return;
            }
            const canOpen = content.offsetWidth > container.offsetWidth;
            emitCanOpenChange(canOpen);
        });
        resizeObserver.observe(containerRef.current);
        resizeObserver.observe(contentRef.current);
        return () => {
            resizeObserver.disconnect();
        };
    }, [contentRef, containerRef, open]);
    const chevronFlip = openDirection === 'down' ? open : !open;
    return (_jsxs(ScrollAreaRoot, Object.assign({ className: clsx('flex flex-col', 'layer-components:max-h-300px', className), background: background, "data-state": open ? 'open' : 'closed' }, rest, { children: [_jsx(ScrollAreaViewport, { ref: containerRef, children: _jsxs("div", { className: clsx('layer-components:px-3 layer-components:pt-3 layer-components:pb-4 layer-components:gap-2', 'flex flex-row items-center gap-2 flex-shrink-0 w-max-content w-auto)', contentClassName), ref: contentRef, children: [_jsx(CollapsibleSimple, { horizontal: true, open: internalCanOpen && !disableInternalOpenToggle, children: _jsx(Button, { onClick: toggleOpen, size: "icon", color: "ghost", className: clsx('flex-shrink-0 bg-[var(--scroll-bg)] sticky left-0 top-2 z-1', !open &&
                                    'rounded-none h-full border-r border-r-solid border-r-gray'), children: _jsx(Icon, { name: "chevron", className: clsx('transition-transform', chevronFlip && 'rotate-180') }) }) }), children] }) }), _jsx(ScrollAreaScrollbar, {}), _jsx(ScrollAreaScrollbar, { orientation: "horizontal" })] })));
}
//# sourceMappingURL=HorizontalList.js.map